import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Flame,
  ShieldCheck,
  Lock,
  FileText,
} from "lucide-react";

/**
 * Premium-feeling legal shell. Used for /terms and /privacy. Keeps the
 * dark-industrial atmosphere — no whitewashed legal-template look.
 */
export default function LegalLayout({ kicker, title, lastUpdated, intro, sections, testid }) {
  return (
    <div data-testid={testid} className="w-full">
      {/* HERO */}
      <section className="relative border-b border-zinc-900 bg-[#070708] overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[radial-gradient(circle,rgba(255,107,26,0.10)_0%,transparent_65%)] pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,107,26,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,107,26,0.4) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative z-10 max-w-[1100px] mx-auto px-6 lg:px-10 pt-32 pb-16">
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <Flame size={14} className="text-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-[#ff6b1a]">
                {kicker}
              </span>
            </div>
            <h1 className="font-display uppercase font-black tracking-[-0.02em] leading-[0.92] text-[clamp(2.4rem,6vw,5rem)]">
              {title}
            </h1>
            <p className="font-display uppercase tracking-[0.22em] text-[11px] text-zinc-400">
              Last updated:{" "}
              <span className="text-[#ff6b1a]">{lastUpdated}</span>
            </p>
            <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-3xl">
              {intro}
            </p>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="relative bg-[#050506] border-b border-zinc-900 overflow-hidden">
        <div className="relative z-10 max-w-[1100px] mx-auto px-6 lg:px-10 py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14">
            {/* TOC sidebar */}
            <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
              <div className="border border-zinc-900 bg-[#0a0a0c] p-6">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-zinc-900">
                  <FileText size={14} className="text-[#ff6b1a]" />
                  <span className="font-display uppercase tracking-[0.32em] text-[10px] text-[#ff6b1a] font-bold">
                    Contents
                  </span>
                </div>
                <ol className="flex flex-col gap-3">
                  {sections.map((s, i) => (
                    <li key={s.heading}>
                      <a
                        href={`#${s.id || `sec-${i + 1}`}`}
                        className="group flex items-baseline gap-3 text-[12px] text-zinc-400 hover:text-[#ff6b1a] transition-colors"
                      >
                        <span className="font-display tabular-nums text-[10px] text-zinc-600 group-hover:text-[#ff6b1a] transition-colors min-w-[20px]">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="font-display uppercase tracking-[0.14em] leading-snug">
                          {s.heading}
                        </span>
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>

            {/* Body */}
            <article className="lg:col-span-8 flex flex-col gap-10">
              {sections.map((s, i) => (
                <section key={s.heading} id={s.id || `sec-${i + 1}`} className="scroll-mt-28">
                  <div className="flex items-baseline gap-4 mb-4">
                    <span className="font-display tabular-nums text-[#ff6b1a] text-sm tracking-[0.3em] font-bold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h2 className="font-display uppercase text-xl md:text-2xl font-black tracking-tight text-white leading-tight">
                      {s.heading}
                    </h2>
                  </div>
                  <div className="flex flex-col gap-4 text-zinc-300 text-[14px] md:text-[15px] leading-[1.7] pl-0 md:pl-9">
                    {s.body.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                    {s.bullets && (
                      <ul className="flex flex-col gap-2 mt-1">
                        {s.bullets.map((b) => (
                          <li
                            key={b}
                            className="flex items-start gap-3 text-zinc-300 text-[14px] leading-[1.65]"
                          >
                            <span className="mt-2 h-1.5 w-1.5 bg-[#ff6b1a] flex-shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>
              ))}
            </article>
          </div>
        </div>
      </section>

      {/* PROPRIETARY FOOTER STRIP */}
      <section className="relative bg-[#070708] border-b border-zinc-900 overflow-hidden">
        <div className="relative z-10 max-w-[1100px] mx-auto px-6 lg:px-10 py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-l-2 border-[#ff6b1a] pl-6">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <ShieldCheck size={14} className="text-[#ff6b1a]" />
                <span className="font-display uppercase tracking-[0.32em] text-[10px] text-[#ff6b1a] font-bold">
                  Proprietary Operational Systems
                </span>
              </div>
              <p className="font-display uppercase tracking-[0.18em] text-[11px] text-zinc-300">
                Field-tested proprietary infrastructure ·{" "}
                <span className="text-zinc-100">Powered by ForgedOps LLC</span>
              </p>
            </div>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 px-7 py-4 bg-[#ff6b1a] text-black font-display uppercase tracking-[0.18em] text-[12px] font-bold hover:bg-[#ff8533] transition-all shadow-[0_0_30px_rgba(255,107,26,0.35)] hover:shadow-[0_0_50px_rgba(255,107,26,0.55)]"
            >
              Contact Operations
              <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* Re-export simple lock icon to keep the footer of legal layouts cohesive */
export { Lock };

